import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareText, Loader2, CheckCircle2, AlertTriangle, Info, Send } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";

/**
 * SmsImportPanel — the "seed my regulars in one tap" widget.
 *
 * Merchant pastes a list of phone numbers (their regulars from an old POS
 * export / customer notebook / iPhone contacts), optionally tweaks the SMS
 * body, and hits send. We normalise every number to E.164 on the server
 * (accepting `0412...`, `+61 (0) 412...`, `61412...`) and blast a
 * personalised join link via Twilio.
 *
 * Design: two panes stacked. Top pane is the paste + preview + send. Bottom
 * pane is the delivery history for this program (last 20 batches with
 * delivered/failed counts). Both live inside the DistributePanel so
 * merchants have every distribution channel in one place.
 */
export function SmsImportPanel({ orgId, program }) {
  const [status, setStatus] = useState({ configured: null }); // null=loading
  const [numbersRaw, setNumbersRaw] = useState("");
  const [messageTemplate, setMessageTemplate] = useState(
    "Hi from {business} — add your loyalty pass to your Apple/Google Wallet in 5s: {join_link}"
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadStatus = () => {
    api.get(`/orgs/${orgId}/sms/status`).then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  };
  const loadHistory = () => {
    api.get(`/orgs/${orgId}/programs/${program.id}/sms-import/history`).then((r) => setHistory(r.data.batches || [])).catch(() => {});
  };
  useEffect(() => { loadStatus(); loadHistory(); /* eslint-disable-next-line */ }, [orgId, program.id]);

  const counts = useMemo(() => {
    const tokens = (numbersRaw || "").split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean);
    return { total: tokens.length, first: tokens.slice(0, 3) };
  }, [numbersRaw]);

  const submit = async () => {
    if (!counts.total) { toast.error("Paste at least one phone number."); return; }
    if (counts.total > 500) { toast.error("Split your list — max 500 per import."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/orgs/${orgId}/programs/${program.id}/sms-import`, {
        numbers_raw: numbersRaw,
        message_template: messageTemplate,
      });
      setResult(data);
      toast.success(`Sent to ${data.delivered} regulars`);
      setNumbersRaw("");
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (program.status !== "live") return null; // panel is meaningless without a live join link

  return (
    <div className="pt-5 border-t border-[#F0EDE5]" data-testid="sms-import-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50 flex items-center gap-1.5">
            <MessageSquareText size={12} strokeWidth={2.2} /> Text your regulars
          </div>
          <p className="text-[12.5px] text-[#25282E]/65 mt-1 leading-relaxed">
            Paste a list of your regulars' phone numbers — we'll SMS each one a personalised join link. Perfect for day one.
          </p>
        </div>
        {status.configured === false && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5 text-[10.5px] font-medium">
            <AlertTriangle size={11} /> Twilio pending
          </div>
        )}
      </div>

      {status.configured === false ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2" data-testid="sms-import-notconfigured">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>Twilio credentials aren't set on the server yet. Once <span className="font-mono">TWILIO_ACCOUNT_SID</span>, <span className="font-mono">TWILIO_AUTH_TOKEN</span> and a sender number are in <span className="font-mono">backend/.env</span>, this panel goes live. The rest of your program still works.</div>
        </div>
      ) : (
        <>
          <label className="block">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[12px] font-medium text-[#0B0D10]/85">Phone numbers</span>
              <span className="text-[11px] text-[#25282E]/50 tabular-nums">
                {counts.total} number{counts.total === 1 ? "" : "s"}{counts.total > 500 && " · over the 500 limit"}
              </span>
            </div>
            <textarea
              data-testid="sms-import-numbers"
              value={numbersRaw}
              onChange={(e) => setNumbersRaw(e.target.value)}
              placeholder={"0412 345 678\n0412 987 654\n+61 490 111 222"}
              rows={5}
              className="w-full rounded-xl border border-[#E8E5DE] focus:border-[#0B0D10] focus:outline-none px-3 py-2 text-[13px] font-mono resize-y"
            />
            <div className="text-[11px] text-[#25282E]/50 mt-1">
              Any format works — one per line, comma-separated, or space-separated. AU numbers auto-prefix +61.
            </div>
          </label>

          <label className="block mt-4">
            <div className="text-[12px] font-medium text-[#0B0D10]/85 mb-1.5">Message ({messageTemplate.length} chars)</div>
            <textarea
              data-testid="sms-import-message"
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              rows={2}
              className="w-full rounded-xl border border-[#E8E5DE] focus:border-[#0B0D10] focus:outline-none px-3 py-2 text-[13px] resize-y"
            />
            <div className="text-[11px] text-[#25282E]/50 mt-1">
              Use <span className="font-mono">{"{business}"}</span> and <span className="font-mono">{"{join_link}"}</span>. Keep it under 160 chars to avoid multi-part billing.
            </div>
          </label>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Button
              data-testid="sms-import-send"
              disabled={busy || counts.total === 0 || counts.total > 500}
              onClick={submit}
              className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 font-semibold px-5 text-[13.5px] disabled:opacity-50"
            >
              {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Blasting…</> : <><Send size={14} className="mr-1.5" />Send join link to {counts.total || "…"} regulars</>}
            </Button>
            {counts.first.length > 0 && (
              <div className="text-[11.5px] text-[#25282E]/55 font-mono">
                First 3: {counts.first.join(", ")}
              </div>
            )}
          </div>

          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-4 rounded-xl border border-[#E8E5DE] bg-[#F4F1E9] p-3 text-[12.5px] text-[#0B0D10]"
                data-testid="sms-import-result"
              >
                <div className="flex items-center gap-1.5 font-medium">
                  <CheckCircle2 size={13} className="text-emerald-600" /> Blast complete
                </div>
                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5">
                  <div><span className="text-[#25282E]/55">Delivered:</span> <span className="tabular-nums font-medium">{result.delivered}</span></div>
                  <div><span className="text-[#25282E]/55">Failed:</span> <span className="tabular-nums font-medium">{result.failed}</span></div>
                  <div><span className="text-[#25282E]/55">Invalid:</span> <span className="tabular-nums font-medium">{result.invalid}</span></div>
                  <div><span className="text-[#25282E]/55">Already invited:</span> <span className="tabular-nums font-medium">{result.already_invited}</span></div>
                </div>
                {result.failures?.length > 0 && (
                  <details className="mt-2 text-[11.5px]">
                    <summary className="cursor-pointer text-[#25282E]/70">See {result.failures.length} failure{result.failures.length === 1 ? "" : "s"}</summary>
                    <ul className="mt-1.5 space-y-0.5 font-mono">
                      {result.failures.slice(0, 10).map((f, i) => (
                        <li key={i} className="text-[#25282E]/75">{f.phone} — {f.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#F0EDE5]" data-testid="sms-import-history">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50 mb-2">Recent blasts</div>
          <ul className="space-y-1.5">
            {history.slice(0, 5).map((b) => (
              <li key={b.batch_id} className="flex items-center justify-between text-[12px]">
                <div className="text-[#0B0D10]/75">
                  {new Date(b.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="text-[#25282E]/70 tabular-nums">
                  <span className="text-emerald-700 font-medium">{b.delivered}</span> delivered
                  {b.failed > 0 && <> · <span className="text-red-700 font-medium">{b.failed}</span> failed</>}
                  <span className="text-[#25282E]/40"> · of {b.recipient_count}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
